import { MutableRefObject, useEffect, useRef, useState } from "react";

/** Props */
type Props = {
  allPositions?: number[];
  handlePosition: (
    pixelPosition: number,
    thumbPosition: number,
    index: number,
    isHandleSelected: boolean
  ) => void;
  handleOverContent: (value: string | number | undefined) => void;

  values: string[] | number[];
};

type TimerProps = Props & {
  indexPosition: number;
};
type ThumbProps = Props & {
  panelRef: MutableRefObject<null>;
};

export function usePositions(panelRef: MutableRefObject<null>, initial: number) {
  const [allPositions, setAllPositions] = useState<number[] | undefined>(
    undefined
  );
  const [pixelPosition, setPixelPosition] = useState<string | undefined>(
    undefined
  );
  const [thumbPosition, setThumbPosition] = useState<string | undefined>(
    undefined
  );

  // find positions of all values
  useEffect(() => {
    if (panelRef?.current) {
      const current = panelRef.current as HTMLDivElement | null;

      if (current) {
        const valuesRef = Array.from(
          current.getElementsByClassName("range__content")
        );
        const parentBounds = current.getBoundingClientRect();

        setAllPositions(
          valuesRef.map((values) => {
            const childBounds = values.getBoundingClientRect();
            return childBounds.right - parentBounds.x;
          })
        );
      }
    }
  }, []);

  // initial position
  useEffect(() => {
    if (allPositions) {
      setPixelPosition(`${allPositions[initial]}px`);
      setThumbPosition(
        `${
          allPositions[initial] -
          (allPositions[0] / 2 + getThumbBounds().width / 2)
        }px`
      );
    }
  }, [allPositions]);

  return {
    panelRef,
    allPositions,
    setAllPositions,
    pixelPosition,
    setPixelPosition,
    thumbPosition,
    setThumbPosition,
  };
}

export function getThumbBounds() {
  const thumb = document.getElementsByClassName("range__thumb")[0];
  return thumb.getBoundingClientRect();
}

export function useTimer({
  allPositions,
  indexPosition,
  handlePosition,
  handleOverContent,
  values,
}: TimerProps) {
  const timeoutRef = useRef(null);

  const [isPlay, setIsPlay] = useState<boolean>(false);
  // timer
  useEffect(() => {
    resetTimeout();

    if (isPlay) {
      if (allPositions) {
        timeoutRef.current = setTimeout(() => {
          const newThumbPosition =
            allPositions[indexPosition] +
            allPositions[0] / 2 -
            getThumbBounds().width / 2;

          handlePosition(
            allPositions[indexPosition + 1],
            newThumbPosition,
            indexPosition + 1,
            true
          );

          handleOverContent(values[indexPosition + 1]);
        }, 500) as any;

        if (indexPosition === allPositions.length - 1) {
          setIsPlay(false);
        }
      }
    }

    return () => {
      resetTimeout();
    };
  }, [isPlay, indexPosition]);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };
  const handlePlay = () => {
    setIsPlay(!isPlay);
  };

  const handleStop = () => {
    if (allPositions) {
      handlePosition(
        allPositions[0],
        allPositions[0] / 2 - getThumbBounds().width / 2,
        0,
        false
      );

      setIsPlay(false);
    }
  };

  return { isPlay, setIsPlay, handlePlay, handleStop };
}

export function useThumb({
  allPositions,
  panelRef,
  handlePosition,
  handleOverContent,
  values,
}: ThumbProps) {
  // Add drag
  const startDragging = (event: globalThis.MouseEvent) => {
    if (allPositions && panelRef?.current) {
      const current = panelRef.current as HTMLDivElement | null;
      const panelBounds = current?.getBoundingClientRect() as DOMRect;
      const x = event.pageX - panelBounds.left;
      const absX = Math.abs(x);

      if (x >= 0 && x <= allPositions[allPositions.length - 1]) {
        const desiredPosition = allPositions.reduce((prev, curr) =>
          Math.abs(prev - absX) < Math.abs(curr - absX) ? prev : curr
        );
        const index = allPositions.indexOf(desiredPosition);

        const newThumbPosition =
          index === 0
            ? allPositions[index] -
              allPositions[0] / 2 -
              getThumbBounds().width / 2
            : allPositions[index - 1] +
              allPositions[0] / 2 -
              getThumbBounds().width / 2;

        handlePosition(desiredPosition, newThumbPosition, index, true);

        handleOverContent(values[index]);
      }
    }
  };

  const stopDragging = () => {
    window.removeEventListener("mousemove", startDragging, false);
    window.removeEventListener("mouseup", stopDragging, false);
  };

  const handleDownThumb = () => {
    window.addEventListener("mousemove", startDragging, false);
    window.addEventListener("mouseup", stopDragging, false);
  };
  return { handleDownThumb };
}
